import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CalendarDays, CheckCircle, Circle, TrendingUp, Target, Users, DollarSign, Loader2 } from 'lucide-react';
import { CJMSClient } from '../../api/cjmsApi';
import { feedbackBus } from '../../services/feedbackBus';

interface MonthlyReviewProps {
    client: CJMSClient;
}

const REVIEW_METRICS = [
    { id: 'revenue', label: 'Revenue Growth', icon: DollarSign },
    { id: 'engagement', label: 'Client Engagement', icon: Users },
    { id: 'goals', label: 'Goal Achievement', icon: Target },
    { id: 'growth', label: 'Growth Opportunities', icon: TrendingUp },
];

export const MonthlyReview: React.FC<MonthlyReviewProps> = ({ client }) => {
    const isEligible = client.currentStage === 'Retention' || client.currentStage === 'Growth';
    const [reviewData, setReviewData] = useState({
        month: new Date().toISOString().slice(0, 7),
        wins: '',
        challenges: '',
        nextSteps: '',
        metrics: Object.fromEntries(REVIEW_METRICS.map(m => [m.id, 0])),
    });
    const [status, setStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

    if (!isEligible) return null;

    const handleSubmit = async () => {
        setStatus('saving');
        await new Promise(r => setTimeout(r, 600));
        feedbackBus.emit('activity_completed', {
            activityLabel: `Monthly review submitted for ${reviewData.month}`,
            clientEmail: client.email,
        });
        setStatus('saved');
        setTimeout(() => setStatus('idle'), 2000);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
        >
            <div className="flex items-center gap-2 p-4 border-b border-slate-100">
                <CalendarDays className="w-4 h-4 text-violet-600" />
                <h3 className="text-sm font-bold text-slate-800">Monthly Review</h3>
                <span className="text-[10px] text-slate-400 ml-auto">{reviewData.month}</span>
            </div>
            <div className="p-4 space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {REVIEW_METRICS.map(metric => {
                        const Icon = metric.icon;
                        return (
                            <div key={metric.id} className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                                <div className="flex items-center gap-1 mb-2">
                                    <Icon className="w-3 h-3 text-violet-600" />
                                    <span className="text-[10px] text-slate-500 font-medium">{metric.label}</span>
                                </div>
                                <div className="flex gap-1">
                                    {[1, 2, 3, 4, 5].map(star => (
                                        <button
                                            key={star}
                                            onClick={() => setReviewData(prev => ({
                                                ...prev,
                                                metrics: { ...prev.metrics, [metric.id]: star }
                                            }))}
                                            className={`w-4 h-4 rounded-full transition-all ${
                                                star <= reviewData.metrics[metric.id]
                                                    ? 'bg-violet-600'
                                                    : 'bg-slate-200 hover:bg-slate-300'
                                            }`}
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="space-y-3">
                    <div>
                        <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Key Wins This Month</label>
                        <textarea
                            value={reviewData.wins}
                            onChange={e => setReviewData(prev => ({ ...prev, wins: e.target.value }))}
                            placeholder="What went well?"
                            className="w-full mt-1 px-3 py-2 text-xs border border-slate-200 rounded-lg resize-none h-16 focus:ring-1 focus:ring-violet-400 outline-none"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Challenges</label>
                        <textarea
                            value={reviewData.challenges}
                            onChange={e => setReviewData(prev => ({ ...prev, challenges: e.target.value }))}
                            placeholder="What needs improvement?"
                            className="w-full mt-1 px-3 py-2 text-xs border border-slate-200 rounded-lg resize-none h-16 focus:ring-1 focus:ring-violet-400 outline-none"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Next Steps</label>
                        <textarea
                            value={reviewData.nextSteps}
                            onChange={e => setReviewData(prev => ({ ...prev, nextSteps: e.target.value }))}
                            placeholder="Action items for next month"
                            className="w-full mt-1 px-3 py-2 text-xs border border-slate-200 rounded-lg resize-none h-16 focus:ring-1 focus:ring-violet-400 outline-none"
                        />
                    </div>
                </div>

                <div className="flex justify-end pt-2 border-t border-slate-100">
                    <button
                        onClick={handleSubmit}
                        disabled={status === 'saving'}
                        className="px-4 py-1.5 text-[11px] font-medium bg-violet-600 text-white rounded-md hover:bg-violet-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                    >
                        {status === 'saving' ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                        Submit Review
                    </button>
                </div>
            </div>
        </motion.div>
    );
};
